<script setup lang="ts">
  import { computed, ref } from 'vue';

  import type { Invoice, Partner, TaskRequirement } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { ButtonGroup } from '@ui/components/button-group';
  import { Separator } from '@ui/components/separator';

  import InvoiceSelect from '@/components/filters/InvoiceSelect.vue';
  import PartnerSelect from '@/components/partners/PartnerSelect.vue';

  const props = defineProps<{
    requirement: TaskRequirement;
    partnerId?: string;
    val?: string;
  }>();

  const emit = defineEmits<{
    (e: 'resolve', value: string): void;
  }>();

  const value = ref(props.val || '');

  const entityType = computed(() => props.requirement.input_spec?.entity_type);

  const resolveIfChanged = (next: string) => {
    if (!next) return;
    if (next === props.val) return;
    emit('resolve', next);
  };

  const selectInvoice = (invoice: Invoice | null) => {
    value.value = invoice?.id ?? '';
    resolveIfChanged(value.value);
  };

  const selectPartner = (partner?: Partner) => {
    value.value = partner?.id ?? '';
    resolveIfChanged(value.value);
  };

  const clear = () => {
    if (!value.value) return;
    value.value = '';
    emit('resolve', '');
  };
</script>

<template>
  <ButtonGroup class="w-full">
    <InvoiceSelect
      v-if="entityType === 'Invoice'"
      class="flex-1"
      :invoice-id="value"
      :partner-id="partnerId"
      trigger-size="sm"
      trigger-class="flex-1 hover:bg-background border-none rounded-tr-none rounded-br-none"
      @update="selectInvoice"
    />

    <PartnerSelect
      v-else-if="entityType === 'Partner'"
      class="flex-1"
      :partner-id="value"
      trigger-size="sm"
      trigger-class="flex-1 hover:bg-background border-none rounded-tr-none rounded-br-none"
      @update="selectPartner"
    />

    <template v-if="value">
      <Separator orientation="vertical" />

      <Button class="bg-background" variant="transparent" size="icon-sm" @click="clear"
        ><i class="fa fa-xmark"
      /></Button>
    </template>
  </ButtonGroup>
</template>
